/**
 * m3 echo Frontend - Teaser-Analyse React Query Hooks
 *
 * Alle Query-Keys enthalten Identitäts-Scope und Mandant, damit Cache-Einträge
 * nie über Mandanten- oder Kontowechsel hinweg wiederverwendet werden.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { teaserApi } from '../services/api';
import { billingApi } from '../services/billing';
import { getIdentityScope } from '../stores/useAuthStore';
import type { TeaserAnalysisCreate } from '../types/teaser';

export const PAGE_SIZE = 20;

export const teaserKeys = {
  all: ['teaser'] as const,
  defaults: (tenantId?: number) =>
    ['teaser', 'defaults', tenantId ?? null, getIdentityScope()] as const,
  models: (tenantId?: number) =>
    ['teaser', 'models', tenantId ?? null, getIdentityScope()] as const,
  analyses: (tenantId?: number, page = 0) =>
    ['teaser', 'analyses', tenantId ?? null, page, getIdentityScope()] as const,
  analysesAll: (tenantId?: number) => ['teaser', 'analyses', tenantId ?? null] as const,
};

/** Liefert true, wenn der Mandant das Modul freigeschaltet hat (Fehler -> false). */
export const useTeaserModuleActive = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['billing', 'my-config', getIdentityScope()],
    queryFn: async () => (await billingApi.getMyConfig()).data,
    staleTime: 30_000,
    retry: false,
  });
  return { active: data?.active_features?.teaser_analysis === true, isLoading };
};

export const useTeaserDefaults = (tenantId: number | undefined, enabled = true) =>
  useQuery({
    queryKey: teaserKeys.defaults(tenantId),
    queryFn: async () => (await teaserApi.getDefaultPrompts(tenantId)).data,
    enabled,
    retry: false,
  });

export const useTeaserModels = (tenantId: number | undefined, enabled = true) =>
  useQuery({
    queryKey: teaserKeys.models(tenantId),
    queryFn: async () => (await teaserApi.getModels(tenantId)).data,
    enabled,
    retry: false,
  });

export const useTeaserAnalyses = (
  tenantId: number | undefined,
  page: number,
  enabled = true
) =>
  useQuery({
    queryKey: teaserKeys.analyses(tenantId, page),
    queryFn: async () =>
      (
        await teaserApi.getAnalyses({
          limit: PAGE_SIZE,
          offset: page * PAGE_SIZE,
          ...(tenantId ? { tenant_id: tenantId } : {}),
        })
      ).data,
    enabled,
    retry: false,
    refetchInterval: (query) =>
      query.state.data?.items.some((a) => a.status === 'pending' || a.status === 'running')
        ? 10000
        : false,
  });

export const useCreateTeaserAnalysis = (tenantId: number | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: TeaserAnalysisCreate) =>
      (await teaserApi.createAnalysis(data, tenantId)).data,
    // Auch bei Fehler (z. B. 503): Das Backend hat die Analyse dann als failed gespeichert
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: teaserKeys.analysesAll(tenantId) });
    },
  });
};

export const useSaveTeaserDefaults = (tenantId: number | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (prompts: { id?: number; content: string }[]) =>
      (await teaserApi.saveDefaultPrompts(prompts, tenantId)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teaser', 'defaults', tenantId ?? null] });
    },
  });
};

/** CSV-Download als Blob, Dateiname aus Content-Disposition. */
export const useDownloadTeaserCSV = (tenantId: number | undefined) =>
  useMutation({
    mutationFn: async (id: number) => {
      const response = await teaserApi.exportCSV(id, tenantId);
      const blob = new Blob([response.data], { type: 'text/csv' });
      const header: string = response.headers?.['content-disposition'] ?? '';
      const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header);
      let filename = `teaser_${id}.csv`;
      if (match) {
        try {
          filename = decodeURIComponent(match[1]);
        } catch {
          filename = match[1];
        }
      }
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  });

type ApiErrorShape = {
  response?: { status?: number; data?: { detail?: unknown } };
};

/** Fehlertext aus einer API-Antwort (detail als String oder Pydantic-Liste). */
export const teaserErrorMessage = (error: unknown, fallback: string): string => {
  const detail = (error as ApiErrorShape)?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    return detail
      .map((d) => (d && typeof d === 'object' && 'msg' in d ? String(d.msg) : String(d)))
      .join('; ');
  }
  return fallback;
};

export const teaserErrorStatus = (error: unknown): number | undefined =>
  (error as ApiErrorShape)?.response?.status;
